import { lazy, useEffect } from 'react';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

// Incubator surfaces are hidden for now (ClickUp 86cab5bnx). The components and
// their routes are kept in the tree for a possible later revival; the imports and
// routes below are commented out and old links redirect to the dashboard.
// import CreateIncubator from './components/CreateIncubator/CreateIncubator';
// import Incubator from './components/Incubator/Incubator';
// import Incubators from './components/Incubators/Incubators';
import { FeatureGuardedRoute } from './components/FeatureGate';
import Layout from './components/Layout/Layout';
import { OnboardingGuard } from './components/OnboardingGuard';
import Profile from './components/Profile/Profile';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { ContractsAdminOnly } from './pages/Contracts/ContractsAdminOnly';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import Signup from './pages/Signup/Signup';
import SignupConfirm from './pages/Signup/SignupConfirm';
import Login from './pages/Login/Login';
import OAuthConsent from './pages/OAuthConsent/OAuthConsent';
import { NoAccess } from './pages/NoAccess/NoAccess';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import VerifyEmailChange from './pages/VerifyEmailChange/VerifyEmailChange';
import { SelectTenant } from './pages/SelectTenant/SelectTenant';
import UserExport from './pages/UserExport/UserExport';
import Users from './pages/users/Users';
import { TenantFeature } from './types/tenant.types';
import reloadOnFail from './utils/reloadOnFails';
import { titleForPath } from './utils/routeTitles';

const authProvider = import.meta.env.VITE_AUTH_PROVIDER || 'local';
const isLocalAuth = authProvider === 'local';

const CsmWorkload = lazy(() => reloadOnFail(() => import('./pages/Organizations/CsmWorkload')));
const Organizations = lazy(() => reloadOnFail(() => import('./pages/Organizations/Organizations')));

// Tasks is a baseline workspace feature (ungated). The CRM pages below are
// gated by TenantFeature.CRM.
const Tasks = lazy(() => reloadOnFail(() => import('./pages/Crm/Tasks/Tasks')));
const WorkQueue = lazy(() => reloadOnFail(() => import('./pages/Crm/WorkQueue/WorkQueue')));
const Companies = lazy(() => reloadOnFail(() => import('./pages/Crm/Companies/Companies')));
const Contacts = lazy(() => reloadOnFail(() => import('./pages/Crm/Contacts/Contacts')));
// The attention queue over what the nightly email sweep derived. Not
// feature-gated, matching its resolver — `openAccountSituations` carries no
// FeatureGateGuard, and a nav item hidden where the API is open would be a
// second, divergent opinion about who may see this.
const Situations = lazy(() => reloadOnFail(() => import('./pages/Situations/Situations')));
const Pipeline = lazy(() => reloadOnFail(() => import('./pages/Crm/Pipeline/Pipeline')));
// Revvly Forms (TenantFeature.FORMS). PublicForm is the logged-out hosted page.
const FormsList = lazy(() => reloadOnFail(() => import('./pages/Forms/FormsList')));
const FormDetail = lazy(() => reloadOnFail(() => import('./pages/Forms/FormDetail')));
const PublicForm = lazy(() => reloadOnFail(() => import('./pages/PublicForm/PublicForm')));
// Contract templates, signed through the same client links as forms with a contract.
const ClauseRegister = lazy(() =>
  reloadOnFail(() => import('./pages/Contracts/clauses/ClauseRegister')),
);
const ContractTemplates = lazy(() =>
  reloadOnFail(() => import('./pages/Contracts/ContractTemplates')),
);
const ContractTemplateEditor = lazy(() =>
  reloadOnFail(() => import('./pages/Contracts/ContractTemplateEditor')),
);
const SignatureRequestPage = lazy(() =>
  reloadOnFail(() => import('./pages/Contracts/SignatureRequestPage')),
);
// The page a signer opens from their email; logged out, like PublicForm.
const SignContract = lazy(() => reloadOnFail(() => import('./pages/SignContract/SignContract')));
const Templates = lazy(() => reloadOnFail(() => import('./pages/Templates/Templates')));
const LegacyTemplatesRedirect = lazy(() =>
  reloadOnFail(() => import('./pages/Templates/LegacyTemplatesRedirect')),
);
// Revvly Support (TenantFeature.SUPPORT). SupportLayout holds the one live
// stream every Support screen shares.
const Library = lazy(() => reloadOnFail(() => import('./pages/Library/Library')));
// Work Inbox (TenantFeature.WORK_INBOX): the person's own mail, sorted. Not the
// Support inbox, which lives under /support and is labelled "Support".
const WorkInbox = lazy(() => reloadOnFail(() => import('./pages/Inbox/WorkInbox')));
const InboxSettings = lazy(() => reloadOnFail(() => import('./pages/Inbox/InboxSettings')));
const SupportLayout = lazy(() => reloadOnFail(() => import('./pages/Support/SupportLayout')));
const SupportInbox = lazy(() => reloadOnFail(() => import('./pages/Support/Inbox/SupportInbox')));
const SupportTicketPage = lazy(() =>
  reloadOnFail(() => import('./pages/Support/Ticket/SupportTicketPage')),
);
const SupportKnowledge = lazy(() =>
  reloadOnFail(() => import('./pages/Support/Knowledge/SupportKnowledge')),
);
const SupportGuidelines = lazy(() =>
  reloadOnFail(() => import('./pages/Support/Guidelines/SupportGuidelines')),
);
const SupportMetrics = lazy(() =>
  reloadOnFail(() => import('./pages/Support/Metrics/SupportMetrics')),
);
const SupportCompare = lazy(() =>
  reloadOnFail(() => import('./pages/Support/Compare/SupportCompare')),
);
const SupportPeople = lazy(() =>
  reloadOnFail(() => import('./pages/Support/People/SupportPeople')),
);
const SupportPersonPage = lazy(() =>
  reloadOnFail(() => import('./pages/Support/People/SupportPersonPage')),
);
const SupportSandboxPage = lazy(() =>
  reloadOnFail(() => import('./pages/Support/Sandbox/SupportSandboxPage')),
);
const WhatsNewPage = lazy(() => reloadOnFail(() => import('./components/WhatsNew/WhatsNewPage')));
const SupportNotificationsPage = lazy(() =>
  reloadOnFail(() => import('./pages/Support/Notifications/SupportNotificationsPage')),
);

const SimpleDashboard = lazy(() =>
  reloadOnFail(() => import('./pages/Dashboards/SimpleDashboard')),
);

const UsageUserPage = lazy(() =>
  reloadOnFail(() =>
    import('./pages/ProductUsage/UsageUserPage').then((m) => ({ default: m.UsageUserPage })),
  ),
);
const UserProfilePage = lazy(() =>
  reloadOnFail(() =>
    import('./pages/ProductUsage/UserProfilePage').then((m) => ({ default: m.UserProfilePage })),
  ),
);
const UsageOverview = lazy(() =>
  reloadOnFail(() =>
    import('./pages/ProductUsage/UsageOverview').then((m) => ({ default: m.UsageOverview })),
  ),
);
const ViewOrganization = lazy(() =>
  reloadOnFail(() => import('./pages/Organizations/ViewOrganization')),
);

const NoMatch = lazy(() => reloadOnFail(() => import('./components/NoMatch/NoMatch')));

const ProductConfig = lazy(() => reloadOnFail(() => import('./pages/ProductConfig/ProductConfig')));

// Tax rate editor: its own route so it outranks the /settings/* catch-all.
const TaxRateEditor = lazy(() => reloadOnFail(() => import('./pages/Settings/Tax/TaxRateEditor')));
// Meters (formerly "Variables") moved out of the Catalog into Settings. The
// editor is reused at /settings/meters/new and /settings/meters/:id; the list
// is the Meters tab in Settings.
const MeterEditor = lazy(() =>
  reloadOnFail(() =>
    import('./pages/Catalog/Variables/VariableEditor').then((m) => ({
      default: m.VariableEditor,
    })),
  ),
);

// What the catalog has learned from imports. It was a tab in Settings; it
// describes catalog items, so it lives under /catalog.
const CatalogLearningPage = lazy(() =>
  reloadOnFail(() => import('./pages/Catalog/CatalogLearningPage')),
);

const CatalogComponents = lazy(() =>
  reloadOnFail(() => import('./pages/PricingSurfaces/CatalogComponentRoutes')),
);

const MarketConfig = lazy(() => reloadOnFail(() => import('./pages/MarketConfig/Market')));

const Renewals = lazy(() => reloadOnFail(() => import('./pages/Renewals/Renewals')));

const Finance = lazy(() => reloadOnFail(() => import('./pages/Finance/Finance')));
const AssistantHome = lazy(() => reloadOnFail(() => import('./pages/Assistant/AssistantHome')));

const InvoiceDetail = lazy(() => reloadOnFail(() => import('./pages/InvoiceDetail/InvoiceDetail')));

const CreateInvoicePage = lazy(() =>
  reloadOnFail(() =>
    import('./pages/Finance/CreateInvoiceModal').then((m) => ({ default: m.CreateInvoicePage })),
  ),
);

// Hidden: Feature flag management page (keeping code for future use)
// const FeatureFlagManager = lazy(() =>
//   reloadOnFail(() => import('./pages/FeatureFlags/FeatureFlagManager')),
// );

const Settings = lazy(() => reloadOnFail(() => import('./pages/Settings/Settings')));
const SlackMapping = lazy(() => reloadOnFail(() => import('./pages/Settings/Slack/SlackMapping')));
const DriveFolderMapping = lazy(() =>
  reloadOnFail(() => import('./pages/Settings/Integrations/DriveFolders/DriveFolderMapping')),
);
const SlackInvite = lazy(() =>
  reloadOnFail(() => import('./pages/Settings/Slack/invite/SlackInvite')),
);
const SlackInvites = lazy(() =>
  reloadOnFail(() => import('./pages/Settings/Slack/invite/SlackInvites')),
);
const OkrCyclePage = lazy(() => reloadOnFail(() => import('./pages/Okr/OkrCycleRoute')));
const MyOkrsPage = lazy(() => reloadOnFail(() => import('./pages/Okr/MyOkrsRoute')));
const CoveragePage = lazy(() => reloadOnFail(() => import('./pages/Okr/CoverageRoute')));
const ObjectiveEditorPage = lazy(() =>
  reloadOnFail(() => import('./pages/Okr/ObjectiveEditorRoute')),
);
const KeyResultEditorPage = lazy(() =>
  reloadOnFail(() => import('./pages/Okr/KeyResultEditorRoute')),
);
const TeamOkrPage = lazy(() => reloadOnFail(() => import('./pages/Okr/TeamOkrPage')));
const KeyResultDetailPage = lazy(() =>
  reloadOnFail(() => import('./pages/Okr/KeyResultDetailPage')),
);
const HypothesesRoute = lazy(() =>
  reloadOnFail(() => import('./pages/Okr/Hypotheses/HypothesesRoute')),
);

const AcceptInvite = lazy(() => reloadOnFail(() => import('./pages/AcceptInvite/AcceptInvite')));
const JoinInvite = lazy(() => reloadOnFail(() => import('./pages/JoinInvite/JoinInvite')));

const TransactionImport = lazy(() =>
  reloadOnFail(() => import('./pages/TransactionImport/TransactionImport')),
);
const ImportHistory = lazy(() => reloadOnFail(() => import('./pages/ImportHistory/ImportHistory')));

const SubscriptionImport = lazy(() =>
  reloadOnFail(() => import('./pages/SubscriptionImport/SubscriptionImport')),
);

const Import = lazy(() => reloadOnFail(() => import('./pages/Import/Import')));
const CrmImport = lazy(() => reloadOnFail(() => import('./pages/CrmImport/CrmImport')));

const SyncResults = lazy(() => reloadOnFail(() => import('./pages/SyncResults/SyncResults')));
const CommitSummary = lazy(() =>
  reloadOnFail(() => import('./pages/SyncResults/Summary/CommitSummary')),
);

const SyncResultsReview = lazy(() =>
  reloadOnFail(() => import('./pages/SyncResults/Review/ReviewFlow')),
);
const SyncResultsSpotCheck = lazy(() =>
  reloadOnFail(() => import('./pages/SyncResults/Review/SpotCheck')),
);

const Onboarding = lazy(() => reloadOnFail(() => import('./pages/Onboarding/OnboardingRoute')));

// Catalog (modules/bundles) + Subscriptions — the unified design surfaces.
// They render inside the main Layout (shared sidebar), not a separate app.
const CatalogOverview = lazy(() =>
  reloadOnFail(() => import('./pages/PricingSurfaces/routes/CatalogOverview')),
);
const CatalogModuleRoutes = lazy(() =>
  reloadOnFail(() => import('./pages/PricingSurfaces/CatalogModuleRoutes')),
);
const CatalogBundleRoutes = lazy(() =>
  reloadOnFail(() => import('./pages/PricingSurfaces/CatalogBundleRoutes')),
);
const Discounts = lazy(() =>
  reloadOnFail(() => import('./pages/PricingSurfaces/routes/DiscountsView')),
);
const SubscriptionRoutes = lazy(() =>
  reloadOnFail(() => import('./pages/PricingSurfaces/SubscriptionRoutes')),
);
const BulkUsageEntry = lazy(() =>
  reloadOnFail(() => import('./pages/MeteredUsage/BulkUsageEntry')),
);

function App() {
  const { pathname } = useLocation();

  // The tab title is what browser history shows, so it names the page.
  useEffect(() => {
    document.title = titleForPath(pathname);
  }, [pathname]);

  return (
    <Routes>
      {/* Public routes - no tenant required */}
      <Route path="/select-tenant" element={<SelectTenant />} />
      <Route path="/no-access" element={<NoAccess />} />
      <Route path="/login" element={isLocalAuth ? <Login /> : <Navigate to="/" replace />} />
      <Route
        path="/oauth/consent"
        element={isLocalAuth ? <OAuthConsent /> : <Navigate to="/" replace />}
      />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/confirm" element={<SignupConfirm />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email-change" element={<VerifyEmailChange />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/join/:token" element={<JoinInvite />} />
      {/* Public hosted / embedded form: no session, scoped by its public id. */}
      <Route path="/f/:publicId" element={<PublicForm />} />
      <Route path="/f/r/:token" element={<PublicForm />} />
      <Route path="/s/:token" element={<SignContract />} />

      {/* Onboarding route - requires auth and tenant but bypasses onboarding check */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      {/* Protected routes - require authentication, tenant selection, and completed onboarding */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <OnboardingGuard>
              <Layout />
            </OnboardingGuard>
          </ProtectedRoute>
        }
      >
        {/* Chat-first home: the assistant is the landing page; the classic
            dashboard stays at /dashboard via the sidebar. */}
        <Route index element={<AssistantHome />} />
        <Route path="/assistant" element={<AssistantHome />} />
        <Route path="/account" element={<Profile />} />
        <Route path="/users" element={<Users />} />
        <Route path="/users/export" element={<UserExport />} />
        {/* Incubator section hidden for now — redirect old links to the dashboard.
            Restore these routes (and the imports above) to revive it. (86cab5bnx) */}
        <Route path="/incubators" element={<Navigate to="/dashboard" replace />} />
        <Route path="/incubator/create" element={<Navigate to="/dashboard" replace />} />
        <Route path="/incubator/:id/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<SimpleDashboard />} />
        {/* Tasks is a baseline workspace feature — available to every tenant,
            not gated by TenantFeature.CRM. The CRM entities below stay gated. */}
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/situations" element={<Situations />} />
        <Route
          path="/library"
          element={
            <FeatureGuardedRoute anyFeature={[TenantFeature.CRM, TenantFeature.SUPPORT]}>
              <Library />
            </FeatureGuardedRoute>
          }
        />
        {/* Templates live in the Library; Templates gates each tab on its own feature. */}
        <Route
          path="/library/templates/*"
          element={
            <FeatureGuardedRoute anyFeature={[TenantFeature.CRM, TenantFeature.FINANCE]}>
              <Templates />
            </FeatureGuardedRoute>
          }
        />
        {/* Work queue works the same tasks one at a time, so it shares their gate. */}
        <Route path="/work-queue" element={<WorkQueue />} />
        <Route
          path="/inbox"
          element={
            <FeatureGuardedRoute feature={TenantFeature.WORK_INBOX}>
              <WorkInbox />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/inbox/settings"
          element={
            <FeatureGuardedRoute feature={TenantFeature.WORK_INBOX}>
              <InboxSettings />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/companies"
          element={
            <FeatureGuardedRoute feature={TenantFeature.CRM}>
              <Companies />
            </FeatureGuardedRoute>
          }
        />
        {/* Renamed from Prospects — keep old bookmarks and links working. */}
        <Route path="/prospects" element={<Navigate to="/companies" replace />} />
        <Route
          path="/contacts"
          element={
            <FeatureGuardedRoute feature={TenantFeature.CRM}>
              <Contacts />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/pipeline"
          element={
            <FeatureGuardedRoute feature={TenantFeature.CRM}>
              <Pipeline />
            </FeatureGuardedRoute>
          }
        />
        {/* Addressable stage editor: same board, settings panel open on arrival,
            so the editor can be linked to instead of only reachable via the
            header button (the page renders the board behind the panel). */}
        {/* Forecast view: every open deal as one inline-editable table. */}
        <Route
          path="/pipeline/forecast"
          element={
            <FeatureGuardedRoute feature={TenantFeature.CRM}>
              <Pipeline />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/pipeline/stages"
          element={
            <FeatureGuardedRoute feature={TenantFeature.CRM}>
              <Pipeline />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/forms"
          element={
            <FeatureGuardedRoute feature={TenantFeature.FORMS}>
              <FormsList />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/forms/:id/:tab?"
          element={
            <FeatureGuardedRoute feature={TenantFeature.FORMS}>
              <FormDetail />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/contracts"
          element={
            <FeatureGuardedRoute feature={TenantFeature.FORMS}>
              <ContractsAdminOnly>
                <ContractTemplates />
              </ContractsAdminOnly>
            </FeatureGuardedRoute>
          }
        />
        {/* Contract records exist with or without Forms, so the register is not gated on it. */}
        <Route
          path="/contracts/clauses"
          element={
            <ContractsAdminOnly>
              <ClauseRegister />
            </ContractsAdminOnly>
          }
        />
        <Route
          path="/contracts/:id"
          element={
            <FeatureGuardedRoute feature={TenantFeature.FORMS}>
              <ContractsAdminOnly>
                <ContractTemplateEditor />
              </ContractsAdminOnly>
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/contracts/sent/:id"
          element={
            <FeatureGuardedRoute feature={TenantFeature.FORMS}>
              <SignatureRequestPage />
            </FeatureGuardedRoute>
          }
        />
        {/* Templates moved to the Library; old links redirect there. Declared
            before the /settings/* catch-all. */}
        <Route path="/templates" element={<LegacyTemplatesRedirect />} />
        <Route path="/templates/*" element={<LegacyTemplatesRedirect />} />
        <Route path="/settings/templates" element={<LegacyTemplatesRedirect />} />
        <Route path="/settings/templates/*" element={<LegacyTemplatesRedirect />} />
        <Route path="/clients" element={<Organizations />} />
        <Route path="/clients/workload" element={<CsmWorkload />} />
        <Route
          path="/product-usage/users/:externalUserId"
          element={
            <FeatureGuardedRoute feature={TenantFeature.PRODUCT_USAGE}>
              <UserProfilePage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/product-usage"
          element={
            <FeatureGuardedRoute feature={TenantFeature.PRODUCT_USAGE}>
              <UsageOverview />
            </FeatureGuardedRoute>
          }
        />
        <Route path="/clients/:id" element={<ViewOrganization />} />
        <Route
          path="/clients/:id/usage/users/:externalUserId"
          element={
            <FeatureGuardedRoute feature={TenantFeature.PRODUCT_USAGE}>
              <UsageUserPage />
            </FeatureGuardedRoute>
          }
        />
        <Route path="/clients/:id/:tab" element={<ViewOrganization />} />
        {/* Subscriptions — unified design surface (list, detail, create, migrate…). */}
        <Route path="/subscriptions/*" element={<SubscriptionRoutes />} />
        {/* Modules & Bundles now live in the unified catalog. */}
        <Route path="/modules" element={<Navigate to="/catalog/modules" replace />} />
        <Route path="/bundles" element={<Navigate to="/catalog/bundles" replace />} />
        <Route
          path="/product-config/modules"
          element={<Navigate to="/catalog/modules" replace />}
        />
        <Route
          path="/product-config/bundles"
          element={<Navigate to="/catalog/bundles" replace />}
        />
        <Route path="/product-config" element={<ProductConfig />} />
        <Route path="/product-config/*" element={<ProductConfig />} />
        <Route path="/market-config" element={<MarketConfig />} />
        {/* Standalone Pricing page removed (ClickUp 86cab5cen) — redirect any
            old bookmarked links to the catalog. */}
        <Route path="/pricing" element={<Navigate to="/catalog" replace />} />
        <Route path="/pricing/*" element={<Navigate to="/catalog" replace />} />
        {/* Catalog — module & bundle surfaces (components/variables unchanged). */}
        <Route path="/catalog" element={<CatalogOverview />} />
        <Route path="/catalog/modules/*" element={<CatalogModuleRoutes />} />
        <Route path="/catalog/bundles/*" element={<CatalogBundleRoutes />} />
        <Route path="/catalog/components/*" element={<CatalogComponents />} />
        <Route path="/catalog/learning" element={<CatalogLearningPage />} />
        <Route path="/settings/catalog" element={<Navigate to="/catalog/learning" replace />} />
        {/* Discounts — first-class page (Reusable / One-time tabs), grouped under
            Catalog in the sidebar but on its own top-level path. */}
        <Route path="/discounts" element={<Discounts />} />
        <Route path="/catalog/discounts" element={<Navigate to="/discounts" replace />} />
        {/* Meters live in Settings now; the editor reuses dedicated routes that
            outrank the /settings/* catch-all, and the old catalog path redirects. */}
        <Route
          path="/settings/tax/rates/new"
          element={
            <FeatureGuardedRoute feature={TenantFeature.TAX} redirectTo="/settings">
              <TaxRateEditor />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/settings/tax/rates/:id"
          element={
            <FeatureGuardedRoute feature={TenantFeature.TAX} redirectTo="/settings">
              <TaxRateEditor />
            </FeatureGuardedRoute>
          }
        />
        <Route path="/settings/meters/new" element={<MeterEditor />} />
        <Route path="/settings/meters/:id" element={<MeterEditor />} />
        <Route path="/catalog/variables" element={<Navigate to="/settings/meters" replace />} />
        <Route path="/catalog/variables/*" element={<Navigate to="/settings/meters" replace />} />
        {/* Legacy variable-pricing path → unified catalog */}
        <Route path="/variable-pricing" element={<Navigate to="/catalog" replace />} />
        <Route path="/variable-pricing/*" element={<Navigate to="/catalog" replace />} />
        <Route
          path="/analytics/subscriptions"
          element={<Navigate to="/dashboard?tab=nrr" replace />}
        />
        <Route path="/renewals" element={<Renewals />} />
        <Route path="/renewals/:tab" element={<Navigate to="/renewals" replace />} />
        <Route path="/renewal-calendar" element={<Navigate to="/renewals" replace />} />
        <Route path="/renewal-queue" element={<Navigate to="/renewals" replace />} />
        <Route path="/renewal-actions" element={<Navigate to="/renewals" replace />} />
        <Route path="/finance" element={<Finance />} />
        <Route path="/usage/bulk-entry" element={<BulkUsageEntry />} />
        <Route path="/invoices/new" element={<CreateInvoicePage />} />
        <Route path="/invoices/:id" element={<InvoiceDetail />} />
        <Route path="/settings/team" element={<Navigate to="/users" replace />} />
        {/* Declared before the /settings/* catch-all so the mapping screen is its
          own route rather than a tab body; it is deep enough to deserve a link. */}
        <Route path="/settings/slack/mapping" element={<SlackMapping />} />
        <Route path="/settings/slack/invite" element={<SlackInvite />} />
        <Route path="/settings/slack/invites" element={<SlackInvites />} />
        {/* Drive client folders: mapping + activity. Before the /settings/* catch-all. */}
        <Route
          path="/settings/integrations/drive-folders"
          element={
            <FeatureGuardedRoute
              feature={TenantFeature.DRIVE_FOLDERS}
              redirectTo="/settings/integrations"
            >
              <DriveFolderMapping />
            </FeatureGuardedRoute>
          }
        />
        {/* The detail and objective routes are declared before the cycle route
          so they are not swallowed by a looser `/okr` match. */}
        <Route
          path="/okr/objectives/new"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <ObjectiveEditorPage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/objectives/:id"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <ObjectiveEditorPage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/objectives/:id/edit"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <ObjectiveEditorPage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/objectives/:objectiveId/key-results/new"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <KeyResultEditorPage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/key-results/:id/edit"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <KeyResultEditorPage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/kr/:id"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <KeyResultDetailPage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/teams/:id"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <TeamOkrPage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/hypotheses"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <HypothesesRoute />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/hypotheses/learnings"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <HypothesesRoute />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/hypotheses/:id"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <HypothesesRoute detail />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <OkrCyclePage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/my-okrs"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <MyOkrsPage />
            </FeatureGuardedRoute>
          }
        />
        <Route
          path="/okr/coverage"
          element={
            <FeatureGuardedRoute feature={TenantFeature.OKR}>
              <CoveragePage />
            </FeatureGuardedRoute>
          }
        />
        {/* /settings/notifications used to redirect to /account, back when the only
          notification setting was the per-device browser push toggle. It now serves
          the tenant's check-in rhythm and digest defaults; the personal half (digest
          day/hour, muted categories, push) still lives on /account. */}
        <Route
          path="/support"
          element={
            <FeatureGuardedRoute feature={TenantFeature.SUPPORT}>
              <SupportLayout />
            </FeatureGuardedRoute>
          }
        >
          <Route index element={<SupportInbox />} />
          {/* Ticket links (notification emails, the bell) use the number. */}
          <Route path="tickets/:number" element={<SupportTicketPage />} />
          <Route path="end-users" element={<SupportPeople />} />
          <Route path="end-users/:id" element={<SupportPersonPage />} />
          <Route path="knowledge" element={<SupportKnowledge />} />
          <Route path="guidelines" element={<SupportGuidelines />} />
          <Route path="metrics" element={<SupportMetrics />} />
          {/* Compare with Intercom (backtest): admins and owners, checked by the API. */}
          <Route path="compare" element={<SupportCompare />} />
          <Route path="notifications" element={<SupportNotificationsPage />} />
          <Route path="sandbox" element={<SupportSandboxPage />} />
        </Route>
        <Route path="/whats-new" element={<WhatsNewPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/*" element={<Settings />} />
        <Route path="/import" element={<Import />} />
        <Route path="/import/history" element={<ImportHistory />} />
        <Route path="/import/transactions" element={<TransactionImport />} />
        <Route path="/import/subscriptions" element={<SubscriptionImport />} />
        <Route
          path="/import/crm"
          element={
            <FeatureGuardedRoute feature={TenantFeature.CRM}>
              <CrmImport />
            </FeatureGuardedRoute>
          }
        />
        <Route path="/import/sync-results/:jobId" element={<SyncResults />} />
        <Route path="/import/sync-results/:jobId/review" element={<SyncResultsReview />} />
        <Route path="/import/sync-results/:jobId/spot-check" element={<SyncResultsSpotCheck />} />
        {/* The same page without a job: autonomy is tenant-wide, so the sample
            can be too. Reached from Settings → Catalog. */}
        <Route path="/import/spot-check" element={<SyncResultsSpotCheck />} />
        <Route path="/import/sync-results/:jobId/summary" element={<CommitSummary />} />
        <Route path="*" element={<NoMatch />} />
      </Route>
    </Routes>
  );
}

export default App;
